import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Save, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { localizedName } from '@/components/localized-name';
import { cn } from '@/lib/utils';
import { EVENT_TYPES, SUBSCRIBER_VISIBILITY } from './event-enums';
import { EventPackagesSection } from './event-packages-section';
import {
  useDeleteEvent,
  useEventHosts,
  useEventLocations,
  useEventServiceTypes,
  useSaveEvent,
  useUploadEventImage,
} from './events.api';
import type { BookingEvent } from './types';

/** The blank event the original's "create new" started from. */
const EMPTY_EVENT: Partial<BookingEvent> = {
  imageUrl: '',
  description: '',
  descriptionEs: '',
  name: '',
  nameEs: '',
  nameEn: '',
  price: 0,
  subscriberVisibility: 1,
  type: 0,
  hostId: '',
  locationId: '',
  isActive: true,
  informationalData: '',
};

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

export function EditEventSheet({
  event,
  open,
  onOpenChange,
}: {
  /** `null` opens the sheet on a blank event. */
  event: BookingEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Partial<BookingEvent>>(EMPTY_EVENT);

  const { data: hosts, isPending: loadingHosts } = useEventHosts(open);
  const { data: locations, isPending: loadingLocations } =
    useEventLocations(open);
  const { data: serviceTypes, isPending: loadingServiceTypes } =
    useEventServiceTypes(open);

  const save = useSaveEvent();
  const remove = useDeleteEvent();
  const upload = useUploadEventImage();

  useEffect(() => {
    if (!open) return;
    setDraft(event ? { ...event } : { ...EMPTY_EVENT });
  }, [event, open]);

  const set = (patch: Partial<BookingEvent>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const isEditing = Boolean(event?.id);

  /*
   * Location, host and service type are required by the API. Rather than let
   * the sheet post a payload the server will refuse, the three selects offer
   * no "None" and the save stays closed until each is answered — named here so
   * the reason is on screen rather than only in a disabled button.
   */
  const missing = [
    !draft.locationId && t('events.location'),
    !draft.hostId && t('events.host'),
    !draft.serviceTypeId && t('events.serviceType'),
  ].filter(Boolean) as string[];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {isEditing ? t('events.editEvent') : t('events.createEvent')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 p-4">
          {isEditing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={remove.isPending}
                >
                  {remove.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 />
                  )}
                  {t('events.deleteEvent')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('events.deleteConfirmTitle')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('events.deleteConfirmBody')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(event!.id, {
                        onSuccess: () => {
                          toast.success(t('events.eventDeleted'));
                          onOpenChange(false);
                        },
                        onError: () => toast.error(t('events.deleteFailed')),
                      })
                    }
                  >
                    {t('events.deleteEvent')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs font-semibold">
              {t('events.isActive')}
            </Label>
            <Switch
              checked={Boolean(draft.isActive)}
              onCheckedChange={(isActive) => set({ isActive })}
            />
          </div>

          <Field label={t('events.internalName')}>
            <Input
              value={draft.name ?? ''}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t('events.name')} EN`}>
              <Input
                value={draft.nameEn ?? ''}
                onChange={(e) => set({ nameEn: e.target.value })}
              />
            </Field>
            <Field label={`${t('events.name')} ES`}>
              <Input
                value={draft.nameEs ?? ''}
                onChange={(e) => set({ nameEs: e.target.value })}
              />
            </Field>
          </div>

          {/* `description` is the English one; `descriptionEs` its pair. The
              asymmetric naming is the API's. */}
          <Field label={`${t('events.description')} EN`}>
            <Textarea
              rows={3}
              value={draft.description ?? ''}
              onChange={(e) => set({ description: e.target.value })}
            />
          </Field>
          <Field label={`${t('events.description')} ES`}>
            <Textarea
              rows={3}
              value={draft.descriptionEs ?? ''}
              onChange={(e) => set({ descriptionEs: e.target.value })}
            />
          </Field>

          <Field label={t('events.informationalData')}>
            <Textarea
              rows={2}
              value={draft.informationalData ?? ''}
              onChange={(e) => set({ informationalData: e.target.value })}
            />
          </Field>

          <Field label={t('events.price')}>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={draft.price ?? 0}
              onChange={(e) => set({ price: Number(e.target.value) })}
            />
          </Field>

          <Field label={t('events.host')}>
            {/* The options load when the sheet opens; until then an existing
                event's value has nothing to show against. */}
            {loadingHosts ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                value={draft.hostId ?? ''}
                onValueChange={(hostId) => set({ hostId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('events.selectHost')} />
                </SelectTrigger>
                <SelectContent>
                  {(hosts ?? []).map((host) => (
                    <SelectItem key={host.id} value={host.id}>
                      {[host.firstName, host.lastName].filter(Boolean).join(' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label={t('events.location')}>
            {loadingLocations ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                value={draft.locationId ?? ''}
                onValueChange={(locationId) => set({ locationId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('events.selectLocation')} />
                </SelectTrigger>
                <SelectContent>
                  {(locations ?? []).map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label={t('events.subscriberVisibility')}>
            <Select
              value={String(draft.subscriberVisibility ?? 1)}
              onValueChange={(v) => set({ subscriberVisibility: Number(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUBSCRIBER_VISIBILITY.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={t('events.typeLabel')}>
            <Select
              value={String(draft.type ?? 0)}
              onValueChange={(v) => set({ type: Number(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={t('events.serviceType')}>
            {loadingServiceTypes ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                value={draft.serviceTypeId ?? ''}
                onValueChange={(serviceTypeId) => set({ serviceTypeId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('events.selectServiceType')} />
                </SelectTrigger>
                <SelectContent>
                  {(serviceTypes ?? []).map((serviceType) => (
                    <SelectItem key={serviceType.id} value={serviceType.id}>
                      {localizedName(serviceType, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label={t('events.image')}>
            <div className="space-y-2">
              {draft.imageUrl && (
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="bg-muted max-h-40 w-full rounded-lg object-contain"
                />
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(fileEvent) => {
                  const file = fileEvent.target.files?.[0];
                  if (!file) return;
                  upload.mutate(file, {
                    onSuccess: (url) => set({ imageUrl: url }),
                    onError: () => toast.error(t('events.uploadFailed')),
                  });
                  fileEvent.target.value = '';
                }}
              />
              <Button
                variant="outline"
                className="w-full border-dashed"
                disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {upload.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <ImagePlus />
                )}
                {t('events.chooseImage')}
              </Button>
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t('events.additionalInformation')} 1 EN`}>
              <Input
                value={draft.additionalInformation1En ?? ''}
                onChange={(e) =>
                  set({ additionalInformation1En: e.target.value })
                }
              />
            </Field>
            <Field label={`${t('events.additionalInformation')} 1 ES`}>
              <Input
                value={draft.additionalInformation1Es ?? ''}
                onChange={(e) =>
                  set({ additionalInformation1Es: e.target.value })
                }
              />
            </Field>
            <Field label={`${t('events.additionalInformation')} 2 EN`}>
              <Input
                value={draft.additionalInformation2En ?? ''}
                onChange={(e) =>
                  set({ additionalInformation2En: e.target.value })
                }
              />
            </Field>
            <Field label={`${t('events.additionalInformation')} 2 ES`}>
              <Input
                value={draft.additionalInformation2Es ?? ''}
                onChange={(e) =>
                  set({ additionalInformation2Es: e.target.value })
                }
              />
            </Field>
          </div>

          {/* Packages hang off an event id, so they appear once there is one
              to hang off — a brand new event is saved first. */}
          {isEditing && <EventPackagesSection eventId={event!.id} />}

          {missing.length > 0 && (
            <p className="text-muted-foreground text-xs">
              {t('events.stillNeeded', { fields: missing.join(', ') })}
            </p>
          )}

          <Button
            className="w-full"
            disabled={save.isPending || missing.length > 0}
            onClick={() =>
              save.mutate(draft, {
                onSuccess: () => {
                  toast.success(t('events.eventSaved'));
                  onOpenChange(false);
                },
                onError: (error) =>
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : t('events.saveFailed')
                  ),
              })
            }
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
            {isEditing ? t('events.update') : t('events.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
